/**
 * Shared presentational components used by both IssueCard (feed) and IssueDetailPage (detail).
 *
 * Only exports React components (no standalone constants) so React Fast Refresh works.
 */
import { WarningFilled } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { darkBlueFontColor, pageBackgroundGrey } from 'assets/styles/variables'
import {
  EMERGENCE_COLORS,
  EMERGENCE_NOTHING_COLOR,
  SAFETY_INDICATOR_COLOR,
  SAFETY_INDICATOR_TOOLTIP,
} from 'features/trend-detection/components/issue-constants'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import type { EmergenceLevel } from 'features/trend-detection/utils/helpers'

interface DetectedIssueIconProps {
  color: string
  width: number
  height: number
}

export const DetectedIssueIcon: React.FC<DetectedIssueIconProps> = ({
  color,
  width,
  height,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 22 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ opacity: 0.8 }}
  >
    <g fill={color} fillRule="evenodd" clipRule="evenodd">
      <path d="m14.3217 3.0754c.3848-.38478 1.0086-.38478 1.3934 0 2.6935 2.69346 2.6935 7.0604 0 9.7539-.3848.3848-1.0086.3848-1.3934 0s-.3848-1.0087 0-1.3934c1.9239-1.92394 1.9239-5.04319 0-6.96709-.3848-.38478-.3848-1.00863 0-1.39341z" />
      <path d="m7.35466 3.07537c.38478.38478.38478 1.00863 0 1.39341-1.9239 1.9239-1.9239 5.04315 0 6.96702.38478.3848.38478 1.0087 0 1.3934-.38478.3848-1.00863.3848-1.39341 0-2.69346-2.6934-2.69346-7.06037 0-9.75383.38478-.38478 1.00863-.38478 1.39341 0z" />
      <path d="m4.56784.288585c.38478.384779.38478 1.008625 0 1.393405-3.46302 3.46302-3.46302 9.07771 0 12.54071.38478.3848.38478 1.0086 0 1.3934s-1.00863.3848-1.39341 0c-4.23257-4.2326-4.23257-11.09494 0-15.327515.38478-.3847798 1.00863-.3847798 1.39341 0z" />
      <path d="m17.1085.28863c.3848-.3847797 1.0087-.3847797 1.3934 0 4.2326 4.23257 4.2326 11.09497 0 15.32747-.3847.3848-1.0086.3848-1.3934 0-.3848-.3847-.3848-1.0086 0-1.3934 3.463-3.463 3.463-9.07765 0-12.54066-.3848-.38478-.3848-1.008631 0-1.39341z" />
      <path d="m7.88231 7.95234c0-1.63248 1.32339-2.95587 2.95589-2.95587s2.9559 1.32339 2.9559 2.95587-1.3234 2.95586-2.9559 2.95586-2.95589-1.32338-2.95589-2.95586z" />
    </g>
  </svg>
)

export const EmergenceBarChart: React.FC<{ level: EmergenceLevel }> = ({
  level,
}) => {
  if (level === 'nothing') {
    const c = EMERGENCE_NOTHING_COLOR
    return (
      <SvgBlock width={20} height={13} viewBox="0 0 24 16">
        <rect x={2} y={10} width={5} height={6} fill={c} rx={1} />
        <rect x={9} y={6} width={5} height={10} fill={c} rx={1} />
        <rect x={16} y={2} width={5} height={14} fill={c} rx={1} />
      </SvgBlock>
    )
  }
  const color = EMERGENCE_COLORS[level]
  return (
    <SvgBlock width={20} height={13} viewBox="0 0 24 16">
      <rect x={2} y={10} width={5} height={6} fill={color} rx={1} />
      <rect
        x={9}
        y={6}
        width={5}
        height={10}
        fill={
          level === 'elevated' || level === 'sharply_elevated'
            ? color
            : '#d9d9d9'
        }
        rx={1}
      />
      <rect
        x={16}
        y={2}
        width={5}
        height={14}
        fill={level === 'sharply_elevated' ? color : '#d9d9d9'}
        rx={1}
      />
    </SvgBlock>
  )
}

const SvgBlock = styled.svg`
  display: block;
`

type ActiveEmergenceLevel = Exclude<EmergenceLevel, 'nothing'>

/**
 * Stepped bar-chart icon used inside the ElevationLevelPopover. Renders one
 * row per tier; the active tier shows the level's color, the others show
 * grey. Distinct from `EmergenceBarChart`, which is the card-header version.
 */
export const EmergenceBarChartForLevel: React.FC<{
  level: ActiveEmergenceLevel
  active: boolean
}> = ({ level, active }) => {
  const c = active ? EMERGENCE_COLORS[level] : '#d9d9d9'
  const heights: Record<ActiveEmergenceLevel, [number, number, number]> = {
    slightly_elevated: [10, 6, 2],
    elevated: [8, 4, 0],
    sharply_elevated: [6, 2, 0],
  }
  const [y1, y2, y3] = heights[level]
  return (
    <SvgBlock width={24} height={16} viewBox="0 0 24 16">
      <rect
        x={2}
        y={y1}
        width={5}
        height={16 - y1}
        fill={active ? c : '#d9d9d9'}
        rx={1}
      />
      <rect
        x={9}
        y={y2}
        width={5}
        height={16 - y2}
        fill={level !== 'slightly_elevated' && active ? c : '#d9d9d9'}
        rx={1}
      />
      <rect
        x={16}
        y={y3}
        width={5}
        height={16 - y3}
        fill={level === 'sharply_elevated' && active ? c : '#d9d9d9'}
        rx={1}
      />
    </SvgBlock>
  )
}

export const SafetyIndicator: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <Tooltip content={SAFETY_INDICATOR_TOOLTIP}>
    <SafetyIconWrap aria-label="Safety issue" role="img" tabIndex={0}>
      <WarningFilled
        style={{ fontSize: size, color: SAFETY_INDICATOR_COLOR }}
      />
    </SafetyIconWrap>
  </Tooltip>
)

const SafetyIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  line-height: 0;
`

export const PostsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const PostItem = styled.div`
  padding: 12px;
  background-color: ${pageBackgroundGrey};
  border-radius: 4px;
  border-left: 3px solid ${TREND_DETECTION_PRIMARY_BLUE};
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const PostMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`

export const PostMetaItem = styled.span`
  font-size: 12px;
  color: ${darkBlueFontColor};
  opacity: 0.7;

  strong {
    opacity: 1;
  }
`

export const PostTextStyled = styled.p<{ $clickable?: boolean }>`
  font-size: 13px;
  color: ${darkBlueFontColor};
  margin: 0;
  font-style: italic;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

  ${(props) =>
    props.$clickable &&
    `
    text-decoration: underline;
    cursor: pointer;
  `}
`
